import { RestError } from '@azure/core-http';
import {
  BatchDiagnostic,
  CarrierUnitDto,
  CourierBatchPrepareDto,
  CourierBatchPreparedDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { castDraft } from 'immer';
import { useCallback, useMemo } from 'react';
import { useImmer } from 'use-immer';
import { downloadToBrowser } from '../../../../../core/util/download-to-browser.util';
import { naturalSort } from '../../../../../core/util/natural-sort.util';
import { useQueryGetCarrierHierarchy } from '../../../../carriers/carriers.queries';
import { useMutationCsvReadFile } from '../../../../users/list/actions/batch-import/batch-import.queries';
import { useMutationImportCourierBatch, useMutationPrepareCourierBatch } from '../../../couriers.queries';
import {
  applyImportResults,
  batchLevelDiagnostics,
  CourierBatchSentCarriers,
  csvSentCarriers,
  CourierBatchAnchor,
  CourierBatchField,
  CourierBatchRow,
  makeCourierCsvTemplate,
  makeCsvPrepareDtos,
  makeImportDtos,
  makePrepareDtos,
  mapPreparedToRows,
  pickPreparedRows,
  resolveBatchDefaultCarrierID
} from '../../../logic/courier-batch.map';
import {
  clearUnmatchedCarriers,
  CourierCsvParseError,
  ParsedCourierCsv,
  editRowValue,
  makeRowIDs,
  markRowsUploading,
  parseCourierCsv,
  selectUploadableRowIDs,
  setAllRowsSelected,
  setRowCarrier,
  toggleRowSelected
} from '../../../logic/courier-batch.utils';
import { BatchImportCouriersActionProps } from './batch-import-couriers-action.component';

export type BatchImportCouriersActionState = {
  step: 'input' | 'review';
  /** What was pasted. Cleared when a file is chosen: one source at a time. */
  pastedText: string;
  fileName?: string;
  fileContent?: string;
  isFileUploadFailure: boolean;
  rows: CourierBatchRow[];
  batchDiagnostics: BatchDiagnostic[];
  /** Headings the API couldn't map to a field, and so ignored. */
  unmapped: string[];
  /** Rows whose carrier from the file matched none of the units, and was cleared. */
  clearedCarrierCount: number;
  hasUploaded: boolean;
  isPreparing: boolean;
  isUploading: boolean;
  /** Rows sent to a validate whose response hasn't come back yet. */
  validatingRowIDs: string[];
  isToastOpen: boolean;
  error?: RestError | Error;
};

export interface BatchImportCouriersActionHandlers {
  onDownloadTemplate(): void;
  onUploadFile(): void;
  onPasteChange(text: string): void;
  onContinue(): void;
  onCellChange(rowID: string, field: CourierBatchField, value: string): void;
  onCarrierChange(rowID: string, unit: CarrierUnitDto | undefined): void;
  onToggleRow(rowID: string): void;
  /** The header's select all. */
  onToggleAll(isSelected: boolean): void;
  onValidate(): void;
  onUpload(): void;
  onCancel(): void;
  onDone(): void;
  onToastClose(): void;
}

const initialState: BatchImportCouriersActionState = {
  step: 'input',
  pastedText: '',
  isFileUploadFailure: false,
  rows: [],
  batchDiagnostics: [],
  unmapped: [],
  clearedCarrierCount: 0,
  hasUploaded: false,
  isPreparing: false,
  isUploading: false,
  validatingRowIDs: [],
  isToastOpen: false
};

export function useBatchImportCouriersAction({ projectID, onAbort, onSuccess }: BatchImportCouriersActionProps) {
  const [state, setState] = useImmer<BatchImportCouriersActionState>(initialState);

  const prepareMutation = useMutationPrepareCourierBatch();
  const importMutation = useMutationImportCourierBatch();
  const readCsvFileMutation = useMutationCsvReadFile();

  const hierarchy = useQueryGetCarrierHierarchy(projectID);
  const units = useMemo(
    () => naturalSort(hierarchy.data?.units ?? []).asc((unit) => unit.name),
    [hierarchy.data?.units]
  );
  const anchor: CourierBatchAnchor = useMemo(
    () => ({ projectID, groupID: resolveBatchDefaultCarrierID(hierarchy.data) }),
    [projectID, hierarchy.data]
  );

  const updateRows = useCallback(
    (update: (rows: CourierBatchRow[], draft: BatchImportCouriersActionState) => CourierBatchRow[]) =>
      setState((draft) => {
        draft.rows = castDraft(update(draft.rows as CourierBatchRow[], draft as BatchImportCouriersActionState));
      }),
    [setState]
  );

  const applyPrepared = useCallback(
    (prepared: CourierBatchPreparedDto, isFirst: boolean, firstSentCarriers?: CourierBatchSentCarriers) =>
      setState((draft) => {
        if (isFirst) {
          const cleared = clearUnmatchedCarriers(mapPreparedToRows(prepared, undefined, firstSentCarriers));
          draft.rows = castDraft(cleared.rows);
          draft.clearedCarrierCount = cleared.cleared;
        }
        const rows = draft.rows as CourierBatchRow[];
        draft.batchDiagnostics = batchLevelDiagnostics(prepared);
        draft.unmapped = prepared.unmapped;
        draft.isPreparing = false;
        draft.validatingRowIDs = [];
        draft.isToastOpen = rows.some((row) => row.status === 'error');
        draft.error = undefined;
        draft.step = 'review';
      }),
    [setState]
  );

  const onPrepareError = useCallback(
    (error: RestError) =>
      setState((draft) => {
        draft.isPreparing = false;
        draft.validatingRowIDs = [];
        draft.error = castDraft(error);
      }),
    [setState]
  );

  async function prepareInRequests(
    dtos: CourierBatchPrepareDto[],
    isFirst: boolean,
    file?: { csv: ParsedCourierCsv; rowIDs: string[] }
  ) {
    if (dtos.length === 0) {
      return;
    }
    setState((draft) => {
      draft.isPreparing = true;
      draft.error = undefined;
      draft.validatingRowIDs = isFirst ? [] : dtos.flatMap((dto) => dto.rowIDs ?? []);
    });
    // Each pass is its own upload: the first request starts it, and the rest are sent under it so
    // their rows are judged against the whole batch — a duplicate split across two requests is caught.
    let uploadID: string | undefined;
    const sentRowIDs: string[] = [];
    try {
      let response: CourierBatchPreparedDto | undefined;
      for (const dto of dtos) {
        response = await prepareMutation.mutateAsync({ ...dto, uploadID });
        uploadID = response.uploadID;
        sentRowIDs.push(...(dto.rowIDs ?? []));
        if (!isFirst) {
          // The response carries every row sent so far, so an earlier row can pick up an error a
          // later request found, as the first of a duplicate.
          const settled = response;
          const returned = new Set(dto.rowIDs);
          setState((draft) => {
            draft.rows = castDraft(mapPreparedToRows(settled, draft.rows as CourierBatchRow[]));
            draft.validatingRowIDs = draft.validatingRowIDs.filter((rowID) => !returned.has(rowID));
          });
        }
      }
      // The last response judged every row against all the others, so it alone is the batch.
      const prepared = pickPreparedRows(response!, file ? file.rowIDs : sentRowIDs);
      // The first prepare's rows name only what the file named; the response carries where they'll go.
      applyPrepared(prepared, isFirst, file && csvSentCarriers(file.csv, file.rowIDs, prepared.mapping));
    } catch (error) {
      onPrepareError(error as RestError);
    }
  }

  // Handed to every grid row, so kept stable.
  const onCellChange = useCallback(
    (rowID: string, field: CourierBatchField, value: string) =>
      updateRows((rows) => rows.map((row) => (row.rowID === rowID ? editRowValue(row, field, value) : row))),
    [updateRows]
  );

  const onCarrierChange = useCallback(
    (rowID: string, unit: CarrierUnitDto | undefined) =>
      updateRows((rows) => rows.map((row) => (row.rowID === rowID ? setRowCarrier(row, unit) : row))),
    [updateRows]
  );

  const onToggleRow = useCallback(
    (rowID: string) => updateRows((rows) => rows.map((row) => (row.rowID === rowID ? toggleRowSelected(row) : row))),
    [updateRows]
  );

  const onToggleAll = useCallback(
    (isSelected: boolean) => updateRows((rows) => setAllRowsSelected(rows, isSelected)),
    [updateRows]
  );

  const handlers: BatchImportCouriersActionHandlers = {
    onDownloadTemplate() {
      downloadToBrowser(makeCourierCsvTemplate(), 'GroundfloorCourierBatchTemplate.csv', 'text/csv');
    },
    onUploadFile() {
      readCsvFileMutation.mutate(undefined, {
        onSuccess(file) {
          setState((draft) => {
            draft.fileName = file.name;
            draft.fileContent = file.content;
            draft.pastedText = '';
            draft.isFileUploadFailure = false;
            draft.error = undefined;
          });
        },
        onError() {
          setState((draft) => {
            draft.isFileUploadFailure = true;
          });
        }
      });
    },
    onPasteChange(text) {
      setState((draft) => {
        draft.pastedText = text;
        draft.fileName = undefined;
        draft.fileContent = undefined;
        draft.isFileUploadFailure = false;
      });
    },
    onContinue() {
      const text = state.fileContent ?? state.pastedText;

      let csv;
      try {
        csv = parseCourierCsv(text);
      } catch (error) {
        setState((draft) => {
          draft.error = error instanceof CourierCsvParseError ? error : new Error('The CSV data could not be read.');
        });
        return;
      }

      const rowIDs = makeRowIDs(csv.rows.length);
      prepareInRequests(makeCsvPrepareDtos(csv, rowIDs, anchor), true, { csv, rowIDs });
    },
    onCellChange,
    onCarrierChange,
    onToggleRow,
    onToggleAll,
    onValidate() {
      if (state.isPreparing || state.isUploading) {
        return;
      }
      prepareInRequests(makePrepareDtos(state.rows, anchor), false);
    },
    async onUpload() {
      const rowIDs = selectUploadableRowIDs(state.rows);
      if (rowIDs.length === 0 || state.isPreparing || state.isUploading) {
        return;
      }

      // Kept so a request that fails outright can put its rows, and those not yet sent, back as they were.
      const sentRows = new Map(state.rows.filter((row) => rowIDs.includes(row.rowID)).map((row) => [row.rowID, row]));
      const dtos = makeImportDtos(state.rows, rowIDs, anchor);

      setState((draft) => {
        draft.isUploading = true;
        draft.error = undefined;
      });
      updateRows((rows) => markRowsUploading(rows, rowIDs));

      // One request after another: each request's rows get their result as it comes back, and the
      // rest keep their spinner. A request that fails outright stops the upload there.
      for (const [index, dto] of dtos.entries()) {
        try {
          const committed = await importMutation.mutateAsync(dto);
          setState((draft) => {
            draft.rows = castDraft(applyImportResults(draft.rows as CourierBatchRow[], committed.results));
            draft.hasUploaded = true;
          });
        } catch (error) {
          const unsent = new Set(dtos.slice(index).flatMap((request) => request.items.map((item) => item.rowID)));
          setState((draft) => {
            draft.rows = castDraft(
              (draft.rows as CourierBatchRow[]).map((row) =>
                unsent.has(row.rowID) ? sentRows.get(row.rowID) ?? row : row
              )
            );
            draft.error = castDraft(error as RestError);
          });
          break;
        }
      }

      setState((draft) => {
        draft.isUploading = false;
      });
    },
    onCancel() {
      onAbort();
    },
    onDone() {
      onSuccess();
    },
    onToastClose() {
      setState((draft) => {
        draft.isToastOpen = false;
      });
    }
  };

  return { state, handlers, units, loading: hierarchy.isLoading || state.isPreparing } as const;
}
